import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Mic, Users, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const features = [
    {
      icon: Mic,
      title: 'Real Conversations',
      description: 'Authentic stories about being millennial Latinas and working mamis, embracing our bilingual identities.',
    },
    {
      icon: Users,
      title: 'Community & Support',
      description: 'Consejos de mujeres a mujeres en un espacio donde no juzgamos - building each other up.',
    },
    {
      icon: Play,
      title: 'Inspiring Content',
      description: 'Topics our culture doesn\'t always discuss openly, shared with honesty and heart.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Welcome Banner */}
      <div className="bg-white border-b border-gray-200 py-3">
        <p className="text-center text-gray-700 text-sm font-medium">
          Welcome to our website. We are happy to have you here!
        </p>
      </div>

      {/* Hero Section */}
      <section className="relative h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_af4289ab-e836-496b-954f-6cfb1ffaac19/artifacts/m8gka077_d365c705-6d67-49e7-9088-4fc5792c7d5f.jpeg)',
          }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center px-4 md:px-6 max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-4 md:mb-6 leading-tight animate-fade-in drop-shadow-lg">
            Dos Bold <span style={{ color: '#b76e79' }}>Latinas</span>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white mb-6 md:mb-8 leading-relaxed drop-shadow-md">
            Millennial Latinas & working mamis figuring it all out
            <br />
            Bilingual conversations, authentic stories, no judgment
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/podcast">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 transition-all duration-300 hover:scale-105 hover:shadow-xl"
                style={{ backgroundColor: '#b76e79' }}
              >
                <Play className="mr-2" size={20} />
                Listen to Podcast
              </Button>
            </Link>
            <Link to="/about">
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 bg-white/10 backdrop-blur-sm text-white border-white hover:bg-white hover:text-black transition-all duration-300 hover:scale-105"
              >
                Learn More
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Achievement Banner */}
      <section className="py-12 px-6 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto">
          <a 
            href="https://goodpods.com/podcasts/dos-bold-latinas-530351" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block"
          >
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1" style={{ background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)' }}>
              <CardContent className="p-12 text-center">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <span className="text-5xl">🏆</span>
                  <span className="px-6 py-2 rounded-full text-lg font-bold text-white" style={{ backgroundColor: '#b76e79' }}>
                    #1 Ranked
                  </span>
                </div>
                <h3 className="text-4xl font-bold mb-4">
                  Top 100 Identity Podcast on Goodpods
                </h3>
                <p className="text-xl text-gray-600 mb-6 leading-relaxed max-w-2xl mx-auto">
                  We're honored to be ranked <strong>#1 on Goodpods' Top 100 Identity All-Time Chart</strong>! 
                  Thank you to our amazing community for your continued support.
                </p>
                <div className="flex items-center justify-center gap-3 text-lg font-medium" style={{ color: '#b76e79' }}>
                  <span>🎧 Listen on Goodpods</span>
                  <span className="text-2xl">→</span>
                </div>
              </CardContent>
            </Card>
          </a>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">
            Why <span style={{ color: '#b76e79' }}>Dos Bold Latinas</span>?
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg max-w-2xl mx-auto">
            Join us, amigas Jatnna Abreu and Rosibel Madera Vialet, as we share personal stories 
            and connect over similar experiences as bilingual (spanglish speaking) women.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={index} 
                  className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white"
                >
                  <CardContent className="p-8 text-center">
                    <div 
                      className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                      style={{ backgroundColor: '#b76e79' }}
                    >
                      <Icon size={32} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 px-6 relative overflow-hidden"
        style={{ backgroundColor: '#b76e79' }}
      >
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Join Our Community
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Consejos de mujeres a mujeres en un espacio donde no juzgamos to simply be able 
            to live our best lives while inspiring and building each other up.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/merchandise">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 bg-white text-black hover:bg-gray-100 transition-all duration-300 hover:scale-105 hover:shadow-xl"
              >
                <ShoppingBag className="mr-2" size={20} />
                Shop Merchandise
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 bg-transparent text-white border-white hover:bg-white hover:text-black transition-all duration-300 hover:scale-105"
              >
                Get in Touch
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;